import {Component, OnInit, Pipe, PipeTransform} from '@angular/core';
import {NgClass, NgForOf, NgIf} from "@angular/common";
import {RouterLink, Router} from "@angular/router";
import {SubscriptionBannerComponent} from "../../common/subscription-banner/subscription-banner.component";
import {DomSanitizer, SafeResourceUrl} from "@angular/platform-browser";
import {PopupService} from "../../services/popup.service";
import {filter, Subscription, take} from "rxjs";
import {
  selectPsyTestError,
  selectPsyTestResult, selectSavePsyTestAnswersError,
  selectSavePsyTestAnswersResult
} from "../../state/psyTesting/psyTesting.selectors";
import {generatePsyTest, savePsyTestAnswers} from '../../state/psyTesting/psyTesting.actions';
import {updateUserTestCode} from '../../state/auth/auth.actions';
import {Store} from "@ngrx/store";
import {FormsModule} from "@angular/forms";
import {MatProgressBar} from "@angular/material/progress-bar";
import {AuthService} from "../../services/auth.service";
import {HeaderComponent} from "../../common/header/header.component";
import {HeaderService} from "../../services/header.service";
import {UserService} from "../../services/user.service";

@Pipe({
  name: 'safe',
  standalone: true // <-- Make the pipe standalone
})
export class SafePipe implements PipeTransform {
  constructor(private sanitizer: DomSanitizer) {}

  transform(value: string): SafeResourceUrl {
    return this.sanitizer.bypassSecurityTrustResourceUrl(value);
  }
}

@Component({
  selector: 'app-psy-testing',
  standalone: true,
    imports: [
        NgIf,
        RouterLink,
        SubscriptionBannerComponent,
        SafePipe,
        FormsModule,
        MatProgressBar,
        NgClass,
        NgForOf,
        HeaderComponent
    ],
  templateUrl: './psy-testing.component.html',
  styleUrl: './psy-testing.component.scss'
})
export class PsyTestingComponent implements OnInit {
  page: number = 1;

  showVideo = false;
  videoUrl!: string;  // Change this to string

  screen: number = 1;
  isConsentGiven: boolean = false;
  progress: number = 0;

  user: any;
  testTypeId: number;
  testUser: any;
  answersMap: { [itemId: number]: number } = {};  // Key-value pairs for itemId and answer

  itemIndex: number = 0; // Track the current item index
  totalItems: number = 0; // Total items across all scales

  constructor(
    private sanitizer: DomSanitizer,
    private authService: AuthService,
    private store: Store,
    private popupService: PopupService,
    private headerService: HeaderService,
    private router: Router,
    private userService: UserService
  ) {

    this.headerService.setHeaderState('PSY Testing', null, true);

    this.authService.authUser().subscribe(user => {
      this.user = user;
    });

  }

  ngOnInit() {
    //
  }

  playVideo() {
    this.showVideo = true;
    this.videoUrl = 'https://www.youtube.com/embed/awWu8k4PQRA?feature=oembed?playlist=awWu8k4PQRA&mute=1&autoplay=1&loop=&controls=1&start=0&end=';
  }

  nextPage(page: number, testTypeId: number) {
    this.page = page
    this.testTypeId = testTypeId;
  }

  viewReport() {
    this.router.navigate(['/psy-testing-report']);
  }

  nextScreen(screen: number) {
    if (this.isConsentGiven) {
      this.screen = screen;
    }

    if (screen === 3) {
      this.screen = 3;
    }
  }

  get currentItem() {
    const allItems = this.testUser.scales.flatMap(scale => scale.items);
    return allItems[this.itemIndex];
  }

  get isLastItem() {
    return this.itemIndex === this.totalItems - 1;
  }

  get isAnswerSelected(): boolean {
    // Check if there is an answer for the current item
    return this.answersMap.hasOwnProperty(this.currentItem.id);
  }

  setAnswer(itemId: number, answer: number) {
    this.answersMap[itemId] = answer; // Save answer in map
  }

  getAnswer(itemId: number): number | undefined {
    return this.answersMap[itemId];
  }

  incrementItemIndex() {
    if (this.itemIndex < this.totalItems - 1) {
      this.itemIndex++;
      this.updateProgress();
    }
  }

  nextItem() {
    if (this.itemIndex < this.totalItems - 1) {
      this.itemIndex++;
      this.updateProgress();
    }
  }

  previousItem() {
    if (this.itemIndex > 0) {
      this.itemIndex--;
      this.updateProgress();
    }
  }

  updateProgress() {
    setTimeout(() => {
      this.progress = Math.round(((this.itemIndex + 1) / this.totalItems) * 100);
    }, 50);
  }

  saveTestAnswers() {
    // console.log('saveTestAnswers')

    const code = this.testUser.code;

    // Dispatch the action to save answers
    this.store.dispatch(savePsyTestAnswers({ code, answers: this.answersMap }));

    // Handle success
    const savePsyTestAnswersResult$ = this.store.select(selectSavePsyTestAnswersResult);
    const savePsyTestAnswersError$ = this.store.select(selectSavePsyTestAnswersError);

    let psyTestAnswersResultSubscription: Subscription = null;
    let psyTestAnswersErrorSubscription: Subscription = null;

    psyTestAnswersResultSubscription = savePsyTestAnswersResult$.pipe(
      filter((result) => result !== null),
      take(1)
    ).subscribe({
      next: (result) => {
        console.log('Answers saved successfully:', result);
        
        // Extract the code from the response and update user's test_code in auth state
        if (result && result.testUser && result.testUser.code) {
          this.store.dispatch(updateUserTestCode({ testCode: result.testUser.code }));
        }
        
        this.popupService.notify('Answers saved successfully!', 'Success', 'success');

        this.nextScreen(3);

        if (psyTestAnswersResultSubscription) {
          psyTestAnswersResultSubscription.unsubscribe();
        }
      },
      error: (error) => {
        console.error('Save answers result subscription error:', error);

        if (psyTestAnswersResultSubscription) {
          psyTestAnswersResultSubscription.unsubscribe();
        }
      }
    });

    // Handle failure
    psyTestAnswersErrorSubscription = savePsyTestAnswersError$.pipe(
      filter((error) => error !== null),
      take(1)
    ).subscribe({
      next: (errorMessage) => {
        this.popupService.notify(errorMessage, 'Error', 'error');

        if (psyTestAnswersErrorSubscription) {
          psyTestAnswersErrorSubscription.unsubscribe();
        }
      },
      error: (error) => {
        console.error('Save answers error subscription error:', error);

        if (psyTestAnswersErrorSubscription) {
          psyTestAnswersErrorSubscription.unsubscribe();
        }
      }
    });
  }

  generatePsyTest() {
    // console.log('generatePsyTest')

    const userId = this.user.id;
    const testTypeId = this.testTypeId;

    // Dispatch the action to generate the psy test
    this.store.dispatch(generatePsyTest({ userId, testTypeId }));

    // Select the result and error from the store
    const psyTestResult$ = this.store.select(selectPsyTestResult);
    const psyTestError$ = this.store.select(selectPsyTestError);

    let psyTestResultSubscription: Subscription = null;
    let psyTestErrorSubscription: Subscription = null;

    // Handle success
    psyTestResultSubscription = psyTestResult$.pipe(
      filter((result) => result !== null),
      take(1)
    ).subscribe({
      next: (result) => {
        // console.log('Psy Test generated successfully:', result);

        this.popupService.notify('Psy Test generated successfully!', 'Success', 'success');

        this.testUser = result.testUser;

        if (this.testUser) {
          this.totalItems = this.testUser.scales.reduce((sum, scale) => sum + scale.items.length, 0);
          localStorage.setItem('psyTestCode', this.testUser.code);
        }

        this.nextScreen(2);

        if (psyTestResultSubscription) {
          psyTestResultSubscription.unsubscribe();
        }
      },
      error: (error) => {
        console.error('Psy Test result subscription error:', error);

        if (psyTestResultSubscription) {
          psyTestResultSubscription.unsubscribe();
        }
      }
    });

    // Handle failure
    psyTestErrorSubscription = psyTestError$.pipe(
      filter((error) => error !== null),
      take(1)
    ).subscribe({
      next: (error) => {
        const errorMessage = error.message || 'Failed to generate Psy Test.';
        this.popupService.notify(errorMessage, 'Error', 'error');

        if (psyTestErrorSubscription) {
          psyTestErrorSubscription.unsubscribe();
        }
      },
      error: (error) => {
        console.error('Psy Test error subscription error:', error);

        if (psyTestErrorSubscription) {
          psyTestErrorSubscription.unsubscribe();
        }
      }
    });
  }

  updateUserTestCode(testCode: string): void {
    // Update the user profile with the test code
    this.userService.updateProfile({ test_code: testCode }).then((response) => {
      console.log('User test_code updated successfully:', response);
      
      // Update the local user object
      if (this.user) {
        this.user.test_code = testCode;
      }
    }).catch((error) => {
      console.error('Failed to update user test_code:', error);
      this.popupService.notify('Failed to save test code to your profile', 'Warning', 'warning');
    });
  }


}
